import { css } from '@/styled-system/css';

export const container = css({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: 0,
  paddingInline: 0,
  paddingTop: 'tight',
  width: '100%',
});

export const heroStack = css({
  display: 'flex',
  flexDirection: 'column',
  gap: 'loose',
  width: '100%',
});

export const decisionGrid = css({
  display: 'flex',
  flexDirection: 'column',
  gap: 'loose',
  width: '100%',
  lg: {
    '&:has(> [data-summary-part="reviews"]:not(:empty))': {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
      rowGap: 'tightest',
      alignItems: 'start',
      '& [data-summary-part="details"]': { display: 'contents' },
      '& [data-summary-part="price-group"]': { display: 'contents' },
      '& [data-summary-part="price"]': { gridColumn: 1, gridRow: 1, alignSelf: 'center' },
      '& [data-summary-part="price-group"] > button': { gridColumn: 1, gridRow: 2 },
      '& [data-summary-part="identity"], & [data-summary-part="facts"]': { gridColumn: 1, marginTop: 'default' },
      '& [data-summary-part="offer"]': { gridColumn: 2, gridRow: '2 / span 3', marginTop: 'default' },
    },
  },
});

export const reviewsSlot = css({
  _empty: { display: 'none' },
  lg: { gridColumn: 2, gridRow: 1, alignSelf: 'center', minWidth: 0 },
});

export const offerSlot = css({ minWidth: 0 });

export const decisionGridWithOffer = css({
  lg: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    alignItems: 'start',
  },
});

export const leftColumn = css({
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: 'loose',
});

// Legacy fallback rendered when shouldHideDetails is true. Older flat
// title style — kept so inactive / hidden listings still surface their
// name in the same visual slot.
export const buildingNameLegacy = css({
  color: 'text.neutral',
  margin: 0,
});

export const identityWithMap = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 'default',
  minWidth: 0,
});

export const identityHeading = css({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: 'tightest',
  minWidth: 0,
  margin: 0,
  color: 'text.neutral',
});

export const identityBuildingName = css({
  minWidth: 0,
  maxWidth: '100%',
  overflowWrap: 'anywhere',
});

export const identityNameRow = css({
  display: 'flex',
  alignItems: 'center',
  gap: 'tightest',
  minWidth: 0,
  maxWidth: '100%',
});

export const identityAddress = css({
  color: 'text.subtle',
  fontSize: 'body',
  fontWeight: 'regular',
  letterSpacing: 'body',
  lineHeight: 'body',
  fontStyle: 'normal',
  overflowWrap: 'anywhere',
});

export const websiteLink = css({
  display: 'inline-flex',
  alignItems: 'center',
  flexShrink: 0,
  color: 'text.accent.brand.hero',
});

export const priceRow = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 'tighter',
  width: '100%',
});

export const priceGroup = css({
  display: 'flex',
  flexDirection: 'column',
  gap: 'tightest',
  minWidth: 0,
});

export const priceInfo = css({
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  columnGap: 'tighter',
  // Replit prototype uses `gap-y-1` (Tailwind = 0.25rem = 4px) on this row.
  rowGap: 'tightest',
});

export const priceText = css({
  fontSize: 'heading-sm',
  fontWeight: 'bold',
  color: 'text.neutral',
  letterSpacing: 'heading-sm',
  lineHeight: 'heading-sm',
});

export const costsLink = css({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 'micro.default',
  background: 'transparent',
  border: 'none',
  padding: 0,
  cursor: 'pointer',
  textAlign: 'left',
  color: 'text.subtle',
  alignSelf: 'flex-start',
  _hover: {
    color: 'text.neutral',
    '& span:first-of-type': {
      textDecoration: 'underline',
    },
  },
});

export const costsLinkText = css({
  fontSize: 'body',
  fontWeight: 'bold',
  letterSpacing: 'body',
  lineHeight: 'body',
});

export const costsLinkChevron = css({
  color: 'text.subtle',
});

export const metaRow = css({
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  columnGap: 'default',
  rowGap: 'tighter',
  fontSize: 'body-sm',
  fontWeight: 'bold',
  color: 'text.neutral',
  letterSpacing: 'body-sm',
  lineHeight: 'body-sm',
});

export const metaItem = css({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 'tighter',
});

export const detailsGroup = css({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: 'tightest',
  minWidth: 0,
});

export const propertyTypeRow = css({
  display: 'flex',
  alignItems: 'center',
  gap: 'tighter',
  flexWrap: 'wrap',
});

export const unitsCount = css({
  fontSize: 'body-sm',
  fontWeight: 'regular',
  color: 'text.subtle',
  letterSpacing: 'body-sm',
  lineHeight: 'body-sm',
});

export const seeFloorPlansLink = css({
  fontSize: 'body-sm',
  fontWeight: 'regular',
  color: 'text.accent.brand.hero',
  letterSpacing: 'body-sm',
  lineHeight: 'body-sm',
  background: 'transparent',
  border: 'none',
  padding: 0,
  cursor: 'pointer',
  textAlign: 'left',
  _hover: { textDecoration: 'underline' },
});

export const otherUnitsLink = css({
  display: 'inline-flex',
  alignItems: 'center',
  alignSelf: 'flex-start',
  color: 'text.accent.brand.hero',
  fontSize: 'body-sm',
  fontWeight: 'bold',
  lineHeight: 'body-sm',
  textDecoration: 'none',
  _hover: {
    textDecoration: 'underline',
  },
});
